import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChartBarIcon,
  TrendingUpIcon,
  CurrencyRupeeIcon,
  GiftIcon,
} from "@heroicons/react/outline";

const RedemptionAnalytics = () => {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState({
    totalRedemptions: 0,
    totalValue: 0,
    averageValue: 0,
    redemptionRate: 0,
    topCategories: [],
    monthlyTrend: [],
  });
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState("month");

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      // Mock data
      setAnalytics({
        totalRedemptions: 45,
        totalValue: 5250000,
        averageValue: 116667,
        redemptionRate: 68.5,
        topCategories: [
          { name: "Necklace", count: 15, value: 2100000 },
          { name: "Ring", count: 12, value: 1050000 },
          { name: "Bangle", count: 10, value: 1400000 },
          { name: "Earring", count: 8, value: 700000 },
        ],
        monthlyTrend: [
          { month: "Jan", count: 5, value: 525000 },
          { month: "Feb", count: 8, value: 840000 },
          { month: "Mar", count: 12, value: 1260000 },
          { month: "Apr", count: 10, value: 1050000 },
          { month: "May", count: 10, value: 1575000 },
        ],
      });
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="bg-background shadow rounded-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-text">Redemption Analytics</h1>
            <p className="mt-1 text-sm text-neutral-dark">
              Insights into redemption patterns and trends
            </p>
          </div>
          <button
            onClick={() => navigate("/jewellery/reports")}
            className="text-neutral-dark hover:text-text"
          >
            ← Back to Reports
          </button>
        </div>
      </div>

      {/* Period Selector */}
      <div className="bg-background shadow rounded-lg p-4 mb-6">
        <div className="flex gap-2">
          {["week", "month", "quarter", "year"].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-4 py-2 rounded-md capitalize ${
                period === p
                  ? "bg-primary text-text-on-primary"
                  : "bg-neutral-light text-text hover:bg-neutral-dark hover:text-text-on-primary"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-primary-light rounded-lg p-3 mr-4">
              <GiftIcon className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Total Redemptions</p>
              <p className="text-2xl font-bold text-text">
                {analytics.totalRedemptions}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-success-light rounded-lg p-3 mr-4">
              <CurrencyRupeeIcon className="h-6 w-6 text-success" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Total Value</p>
              <p className="text-2xl font-bold text-text">
                {formatAmount(analytics.totalValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-secondary-light rounded-lg p-3 mr-4">
              <ChartBarIcon className="h-6 w-6 text-secondary" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Average Value</p>
              <p className="text-2xl font-bold text-text">
                {formatAmount(analytics.averageValue)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="bg-warning-light rounded-lg p-3 mr-4">
              <TrendingUpIcon className="h-6 w-6 text-warning" />
            </div>
            <div>
              <p className="text-sm text-neutral-dark">Redemption Rate</p>
              <p className="text-2xl font-bold text-text">
                {analytics.redemptionRate}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Categories */}
        <div className="bg-background shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-text mb-4">
            Top Categories
          </h3>
          <div className="space-y-3">
            {analytics.topCategories.map((category, index) => (
              <div key={index}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm text-text">{category.name}</span>
                  <span className="text-sm font-medium text-text">
                    {formatAmount(category.value)}
                  </span>
                </div>
                <div className="w-full bg-neutral-light rounded-full h-2">
                  <div
                    className="bg-primary h-2 rounded-full"
                    style={{
                      width: `${(category.value / analytics.totalValue) * 100}%`,
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Trend */}
        <div className="bg-background shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-text mb-4">
            Monthly Trend
          </h3>
          <div className="space-y-2">
            {analytics.monthlyTrend.map((month, index) => (
              <div key={index} className="flex items-center">
                <span className="text-sm text-neutral-dark w-12">
                  {month.month}
                </span>
                <div className="flex-1 mx-3">
                  <div className="w-full bg-neutral-light rounded-full h-6">
                    <div
                      className="bg-success h-6 rounded-full flex items-center justify-end pr-2"
                      style={{
                        width: `${(month.value / Math.max(...analytics.monthlyTrend.map(m => m.value))) * 100}%`,
                      }}
                    >
                      <span className="text-xs text-text-on-primary">
                        {month.count}
                      </span>
                    </div>
                  </div>
                </div>
                <span className="text-sm font-medium text-text w-24 text-right">
                  {formatAmount(month.value)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RedemptionAnalytics;
